import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { API } from "@/utils/api";
import { toast } from "sonner";
import {
  ArrowLeft, Download, CreditCard, Link as LinkIcon, MessageCircle,
  Loader2, AlertCircle, ChevronDown, ChevronRight,
} from "lucide-react";
import { TrackingTimeline } from "@/pages/public/TrackShipment";

const STATUS_BADGE = {
  UNPAID: "bg-red-100 text-red-700 border-red-200",
  PAID: "bg-emerald-100 text-emerald-700 border-emerald-200",
  PARTIAL: "bg-amber-100 text-amber-700 border-amber-200",
  WAIVED: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);

  const [markOpen, setMarkOpen] = useState(false);
  const [markAmount, setMarkAmount] = useState("");
  const [markRef, setMarkRef] = useState("");
  const [markMethod, setMarkMethod] = useState("BANK_TRANSFER");

  const [trackingOpen, setTrackingOpen] = useState(false);
  const [trackingEvents, setTrackingEvents] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/v2/invoices/${id}`);
      setInv(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const loadTracking = async (awb) => {
    if (!awb || trackingEvents || trackingLoading) return;
    setTrackingLoading(true);
    try {
      const r = await api.get(`/v2/tracking/${encodeURIComponent(awb)}/full`);
      setTrackingEvents(r.data?.events || []);
    } catch (e) {
      setTrackingEvents([]);
    }
    setTrackingLoading(false);
  };

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem("sendtoo_token");
      const r = await fetch(`${API}/v2/invoices/${inv.invoice_number}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Sendtoo_${inv.invoice_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF download failed");
    }
  };

  const submitMarkPaid = async () => {
    try {
      await api.put(`/v2/invoices/${inv.invoice_number}/mark-paid`, {
        payment_reference: markRef,
        amount: Number(markAmount),
        payment_method: markMethod,
      });
      toast.success("Marked as paid");
      setMarkOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const sendPaymentLink = async () => {
    try {
      const r = await api.post("/v2/payments/links/create", {
        invoice_id: inv.id,
        customer_id: inv.customer_id,
        expiry_hours: 24,
      });
      const link = r.data?.payment_link || r.data?.link_url || r.data?.url;
      if (link) {
        try {
          await navigator.clipboard.writeText(link);
        } catch (err) {
          // clipboard unavailable
        }
        toast.success("Payment link copied!");
      } else {
        toast.success("Link created");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const sendWhatsApp = async () => {
    try {
      const r = await api.post("/v2/payments/links/create", {
        invoice_id: inv.id,
        customer_id: inv.customer_id,
        expiry_hours: 24,
      });
      const link = r.data?.payment_link || r.data?.link_url || r.data?.url || "";
      const phone = (inv.customer_mobile || "").replace(/\D/g, "");
      const msg = `Hi, your Sendtoo invoice ${inv.invoice_number} of ₹${Number(
        inv.total_amount || 0
      ).toFixed(2)} is ready. Pay here: ${link}`;
      const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
      window.open(url, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading invoice…
        </div>
      </DashboardLayout>
    );
  }
  if (!inv) {
    return (
      <DashboardLayout>
        <div className="text-center py-20">
          <AlertCircle className="w-10 h-10 mx-auto mb-2 text-slate-400" />
          <p className="text-slate-500">Invoice not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const items = inv.line_items || [];
  const due = Math.max(Number(inv.total_amount || 0) - Number(inv.amount_paid || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="invoice-detail-page">
        <button
          onClick={() => navigate("/admin/invoices")}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"
          data-testid="back-to-invoices-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Invoices
        </button>

        {/* Header actions */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{inv.invoice_number}</h1>
            <p className="text-sm text-slate-500">
              Created {new Date(inv.invoice_date || inv.created_at).toLocaleString()}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={downloadPdf} data-testid="dl-pdf-btn">
              <Download className="w-4 h-4 mr-1" /> Download PDF
            </Button>
            {inv.payment_status !== "PAID" && inv.payment_status !== "WAIVED" && (
              <>
                <Button
                  onClick={() => {
                    setMarkAmount(due.toFixed(2));
                    setMarkRef("");
                    setMarkOpen(true);
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="mark-paid-btn"
                >
                  <CreditCard className="w-4 h-4 mr-1" /> Mark Paid
                </Button>
                <Button variant="outline" onClick={sendPaymentLink} data-testid="send-link-btn">
                  <LinkIcon className="w-4 h-4 mr-1" /> Send Payment Link
                </Button>
                <Button variant="outline" onClick={sendWhatsApp} data-testid="send-whatsapp-btn">
                  <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Invoice card replicating PDF layout */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          {/* Header */}
          <div className="flex justify-between items-start border-b border-slate-200 pb-4 mb-4">
            <div>
              <h2 className="text-xl font-bold text-slate-800">SENDTOO LOGISTICS</h2>
              <p className="text-xs text-slate-500">Capexlite Logistics Solutions Pvt Ltd</p>
              <p className="text-xs text-slate-500">GSTIN: {inv.company_gstin || "—"}</p>
            </div>
            <div className="text-right">
              <span
                className={`inline-block text-[11px] font-bold px-2 py-1 rounded border ${
                  STATUS_BADGE[inv.payment_status] || STATUS_BADGE.UNPAID
                }`}
              >
                {inv.payment_status}
              </span>
              <p className="text-xs text-slate-500 mt-1">Invoice Date</p>
              <p className="text-sm font-semibold">
                {new Date(inv.invoice_date || inv.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* 3-column block */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5 text-sm">
            <Block title="Bill To">
              <div className="font-semibold text-slate-800">
                {inv.customer_name || "—"}
              </div>
              <div className="text-slate-600 text-xs whitespace-pre-line">
                {inv.bill_to_address || "—"}
              </div>
              <div className="text-slate-500 text-xs mt-1">
                GSTIN: {inv.customer_gstin || "Unregistered"}
              </div>
            </Block>
            <Block title="Ship From">
              <div className="text-slate-700 whitespace-pre-line">
                {inv.ship_from_address || "—"}
              </div>
            </Block>
            <Block title="Ship To">
              <div className="text-slate-700 whitespace-pre-line">
                {inv.ship_to_address || "—"}
              </div>
            </Block>
          </div>

          {/* Items */}
          <div className="border border-slate-200 rounded-lg overflow-hidden mb-4">
            <table className="w-full text-sm" data-testid="invoice-items-table">
              <thead className="bg-slate-100 text-xs uppercase text-slate-600">
                <tr>
                  <th className="text-left p-2">#</th>
                  <th className="text-left p-2">Description</th>
                  <th className="text-center p-2">HSN</th>
                  <th className="text-right p-2">Qty</th>
                  <th className="text-right p-2">Rate</th>
                  <th className="text-right p-2">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it, idx) => (
                  <tr key={idx}>
                    <td className="p-2">{idx + 1}</td>
                    <td className="p-2">{it.description}</td>
                    <td className="p-2 text-center font-mono text-xs">{it.hsn || "—"}</td>
                    <td className="p-2 text-right">{it.quantity ?? 1}</td>
                    <td className="p-2 text-right">₹{Number(it.rate || 0).toFixed(2)}</td>
                    <td className="p-2 text-right font-semibold">
                      ₹{Number(it.amount || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Tax summary */}
          <div className="flex justify-end mb-4">
            <div className="w-full md:w-1/2 lg:w-1/3 space-y-1 text-sm">
              <Row label="Taxable Amount" value={inv.taxable_amount} />
              {inv.tax_type === "CGST_SGST" ? (
                <>
                  <Row
                    label={`CGST @ ${inv.cgst_pct}%`}
                    value={inv.cgst_amount}
                  />
                  <Row
                    label={`SGST @ ${inv.sgst_pct}%`}
                    value={inv.sgst_amount}
                  />
                </>
              ) : (
                <Row
                  label={`IGST @ ${inv.igst_pct}%`}
                  value={inv.igst_amount}
                />
              )}
              <div className="flex justify-between border-t border-slate-300 pt-2 mt-2 text-base font-bold text-emerald-700">
                <span>Total</span>
                <span>₹{Number(inv.total_amount || 0).toFixed(2)}</span>
              </div>
              {Number(inv.amount_paid || 0) > 0 && (
                <Row label="Amount Paid" value={inv.amount_paid} />
              )}
              {due > 0 && (
                <div className="flex justify-between text-red-700 font-bold pt-1">
                  <span>Amount Due</span>
                  <span>₹{due.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Amount in words */}
          {inv.amount_in_words && (
            <div className="text-xs italic text-slate-600 border-t border-slate-200 pt-3">
              <span className="font-semibold not-italic">Amount in words:</span>{" "}
              {inv.amount_in_words}
            </div>
          )}

          {/* Bank Details */}
          {inv.bank_details && (
            <div className="mt-4 border-t border-slate-200 pt-3">
              <h4 className="text-xs uppercase font-bold text-slate-500 mb-2">
                Bank Details
              </h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-700">
                <div><span className="text-slate-500">Bank:</span> {inv.bank_details.bank_name}</div>
                <div><span className="text-slate-500">A/c Holder:</span> {inv.bank_details.account_holder}</div>
                <div><span className="text-slate-500">A/c Number:</span> {inv.bank_details.account_number}</div>
                <div><span className="text-slate-500">IFSC:</span> {inv.bank_details.ifsc_code}</div>
                <div><span className="text-slate-500">Branch:</span> {inv.bank_details.branch}</div>
                <div><span className="text-slate-500">UPI:</span> {inv.bank_details.upi_id}</div>
              </div>
            </div>
          )}
        </div>

        {/* Shipment Tracking — collapsible */}
        {inv.awb && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="invoice-tracking-section">
            <button
              onClick={() => {
                const next = !trackingOpen;
                setTrackingOpen(next);
                if (next) loadTracking(inv.awb);
              }}
              className="w-full flex items-center justify-between p-4 text-left"
              data-testid="invoice-tracking-toggle"
            >
              <div className="flex items-center gap-2">
                {trackingOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                <span className="font-bold text-slate-800">Shipment Tracking</span>
                {trackingEvents != null && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 border border-teal-200" data-testid="invoice-tracking-count">
                    {trackingEvents.length} events
                  </span>
                )}
                <span className="text-xs text-slate-400 ml-2 font-mono">AWB {inv.awb}</span>
              </div>
              <span className="text-xs text-slate-500">{trackingOpen ? "Hide" : "Expand"}</span>
            </button>
            {trackingOpen && (
              <div className="border-t border-slate-100 p-4">
                {trackingLoading ? (
                  <div className="text-sm text-slate-400 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading tracking…
                  </div>
                ) : trackingEvents && trackingEvents.length > 0 ? (
                  <TrackingTimeline events={trackingEvents} embedded />
                ) : (
                  <div className="text-sm text-slate-400">No tracking events yet.</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {markOpen && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          onClick={() => setMarkOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl p-5 w-full max-w-md shadow-2xl"
            data-testid="mark-paid-modal"
          >
            <h3 className="text-lg font-bold text-slate-800 mb-3">Mark Invoice Paid</h3>
            <div className="space-y-3">
              <Field label="Amount Paid">
                <Input
                  type="number"
                  value={markAmount}
                  onChange={(e) => setMarkAmount(e.target.value)}
                  data-testid="markpaid-amount-input"
                />
              </Field>
              <Field label="Payment Reference">
                <Input
                  value={markRef}
                  onChange={(e) => setMarkRef(e.target.value)}
                  placeholder="UTR / Txn ID"
                />
              </Field>
              <Field label="Payment Method">
                <select
                  value={markMethod}
                  onChange={(e) => setMarkMethod(e.target.value)}
                  className="w-full border border-slate-300 rounded h-10 px-2 text-sm bg-white"
                >
                  <option value="BANK_TRANSFER">Bank Transfer</option>
                  <option value="UPI">UPI</option>
                  <option value="CASH">Cash</option>
                  <option value="CHEQUE">Cheque</option>
                  <option value="OTHER">Other</option>
                </select>
              </Field>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setMarkOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={submitMarkPaid}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="markpaid-confirm-btn"
              >
                Confirm Payment
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Block({ title, children }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-slate-600">
      <span>{label}</span>
      <span>₹{Number(value || 0).toFixed(2)}</span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
